/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { type MessageDescriptor, FormattedMessage } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

type FooterLinkItem = {
	label: MessageDescriptor;
	/**
	 * Destination URL for the link. Set to the literal string `'TODO'`
	 * for items whose final destination has not yet been decided; these
	 * placeholders will be replaced before the footer is wired up to
	 * real anchor `href`s.
	 */
	url: string;
};

const ITEMS: FooterLinkItem[] = [
	{ label: messages.technicalSupport, url: 'https://support.atlassian.com/' },
	{
		label: messages.purchasingLicensing,
		url: 'https://www.atlassian.com/licensing/purchase-licensing',
	},
	{ label: messages.atlassianCommunity, url: 'https://community.atlassian.com/' },
	{ label: messages.knowledgeBase, url: 'https://confluence.atlassian.com/kb' },
	{ label: messages.marketplace, url: 'https://marketplace.atlassian.com/' },
	{ label: messages.myAccount, url: 'https://my.atlassian.com/products/index' },
];

// Destination URL for the "Create support ticket →" CTA, taken from
// atlassian.com's footer.
const CREATE_SUPPORT_TICKET_URL = 'https://support.atlassian.com/contact/';

const styles = cssMap({
	column: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		alignSelf: 'stretch',
		flex: '1 0 0',
		gap: token('space.300'),
	},
	columnInner: {
		display: 'flex',
		flexDirection: 'column',
		alignSelf: 'stretch',
		gap: token('space.300'),
	},
	heading: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.semibold'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// Figma spec uses `Neutral/Light/400` (#B7B9BE) for muted text
		// on the dark `#18191A` footer surface. There is no semantic
		// ADS token for muted text on a custom dark surface, so the
		// hex is inlined to match the design exactly.
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		margin: 0,
	},
	items: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		alignSelf: 'stretch',
		margin: 0,
		padding: 0,
		listStyle: 'none',
	},
	itemLink: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		textDecoration: 'none',
		textAlign: 'left',
	},
	cta: {
		fontFamily: token('font.family.body'),
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '12px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '36px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#B7B9BE',
		textDecoration: 'none',
		margin: 0,
		alignSelf: 'flex-start',
	},
});

/**
 * "Resources" column of the microsite footer.
 *
 * Includes a "Create support ticket →" CTA pinned to the bottom of
 * the column per the Figma design.
 *
 * Currently presentational - real anchor `href`s will be added in a
 * follow-up change once destination URLs are decided.
 */
export const ResourcesColumn = (): JSX.Element => (
	<Box xcss={styles.column}>
		<Box xcss={styles.columnInner}>
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<p css={styles.heading}>
				<FormattedMessage {...messages.heading} />
			</p>
			<ul css={styles.items}>
				{ITEMS.map((item) => (
					<li key={item.label.id}>
						{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
						<a css={styles.itemLink} href={item.url}>
							<FormattedMessage {...item.label} />
						</a>
					</li>
				))}
			</ul>
		</Box>
		{/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
		<a css={styles.cta} href={CREATE_SUPPORT_TICKET_URL}>
			<FormattedMessage {...messages.createSupportTicket} />
		</a>
	</Box>
);
